import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Loader2, AlertCircle, Download, RefreshCw, Clock, MessageSquare, Users } from 'lucide-react'
import { MetricCard } from '@/components/dashboard'
import {
  AttendantMetricsCard,
  AttendantMetricsTable,
  CompactRankingTable,
  CompactFilterBar,
  MessageHistogramChart,
  PeriodComparisonCard,
  SaveSnapshotButton,
} from '@/components/attendance-metrics'
import {
  useAttendanceMetrics,
  useAttendantMetrics,
  useAttendantRankings,
  useExportMetrics,
  useMessageHistogram,
} from '@/hooks/useAttendanceMetrics'
import { toast } from 'sonner'

function formatTime(seconds: number | null): string {
  if (seconds === null) return 'N/A'
  if (seconds < 60) return `${seconds.toFixed(0)}s`
  return `${(seconds / 60).toFixed(1)}min`
}

function formatTrend(value: number | undefined): string | undefined {
  if (value === undefined) return undefined
  const sign = value >= 0 ? '+' : ''
  return `${sign}${value.toFixed(1)}%`
}

export function AttendanceMetricsPage() {
  // Date filters (default: last 7 days)
  const [startDate, setStartDate] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() - 7)
    return date.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0])
  const [compareStartDate, setCompareStartDate] = useState<string | undefined>()
  const [compareEndDate, setCompareEndDate] = useState<string | undefined>()
  const [attendantId, setAttendantId] = useState<string | undefined>()

  // Queries
  const metricsQuery = useAttendanceMetrics({
    startDate,
    endDate,
    compareStartDate,
    compareEndDate,
    attendantId: attendantId ? parseInt(attendantId) : undefined,
  })
  const attendantsQuery = useAttendantMetrics({ startDate, endDate })
  const rankingsQuery = useAttendantRankings({ startDate, endDate })
  const histogramQuery = useMessageHistogram(startDate, endDate, 'day')

  // Mutations
  const exportMutation = useExportMetrics()

  // Handlers
  const handleExport = async () => {
    try {
      await exportMutation.mutateAsync({ startDate, endDate })
      toast.success('CSV exportado com sucesso!')
    } catch (error) {
      toast.error('Erro ao exportar CSV')
    }
  }

  const handleRefreshAll = async () => {
    try {
      await Promise.all([
        metricsQuery.refetch(),
        attendantsQuery.refetch(),
        rankingsQuery.refetch(),
        histogramQuery.refetch(),
      ])
      toast.success('Dados atualizados!')
    } catch (error) {
      toast.error('Erro ao atualizar dados')
    }
  }

  // Loading state
  if (metricsQuery.isLoading || attendantsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando métricas...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (metricsQuery.error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <div>
            <h3 className="text-lg font-semibold mb-2">Erro ao carregar métricas</h3>
            <p className="text-sm text-muted-foreground">
              {metricsQuery.error instanceof Error
                ? metricsQuery.error.message
                : 'Ocorreu um erro inesperado'}
            </p>
          </div>
          <Button onClick={() => metricsQuery.refetch()}>Tentar novamente</Button>
        </div>
      </div>
    )
  }

  const data = metricsQuery.data
  const attendants = attendantsQuery.data || []
  const rankings = rankingsQuery.data || null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Dashboard de Métricas</h1>
          <p className="text-muted-foreground">Performance dos atendentes do Whaticket</p>
        </div>
        <div className="flex items-center gap-2">
          <SaveSnapshotButton
            startDate={startDate}
            endDate={endDate}
            hasData={!!(data && data.totalMessages > 0)}
          />
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefreshAll}
            disabled={
              metricsQuery.isFetching || attendantsQuery.isFetching || rankingsQuery.isFetching
            }
            title="Atualizar dados"
          >
            {metricsQuery.isFetching || attendantsQuery.isFetching || rankingsQuery.isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Filters - Compact Layout */}
      <CompactFilterBar
        startDate={startDate}
        endDate={endDate}
        compareStartDate={compareStartDate}
        compareEndDate={compareEndDate}
        attendantId={attendantId}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onCompareStartDateChange={setCompareStartDate}
        onCompareEndDateChange={setCompareEndDate}
        onAttendantIdChange={setAttendantId}
        onClearComparison={() => {
          setCompareStartDate(undefined)
          setCompareEndDate(undefined)
        }}
        attendants={attendants.map((a) => ({ id: a.attendantOdataId, name: a.attendantName }))}
      />

      {/* KPI Cards */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        <MetricCard
          title="FRT Médio"
          value={formatTime(data?.frtAvg || null)}
          description="First Response Time"
          icon={Clock}
          trend={
            data?.trend?.frt
              ? { value: formatTrend(data.trend.frt) || '', isPositive: data.trend.frt < 0 }
              : undefined
          }
        />
        <MetricCard
          title="TMR Médio"
          value={formatTime(data?.tmrAvg || null)}
          description="Tempo Médio de Resposta"
          icon={Clock}
          trend={
            data?.trend?.tmr
              ? { value: formatTrend(data.trend.tmr) || '', isPositive: data.trend.tmr < 0 }
              : undefined
          }
        />
        <MetricCard
          title="Mensagens"
          value={(data?.totalMessages || 0).toLocaleString()}
          description="Total de mensagens"
          icon={MessageSquare}
          trend={
            data?.trend?.messages
              ? {
                  value: formatTrend(data.trend.messages) || '',
                  isPositive: data.trend.messages > 0,
                }
              : undefined
          }
        />
        <MetricCard
          title="Respostas"
          value={(data?.totalResponses || 0).toLocaleString()}
          description="Grupos de respostas"
          icon={MessageSquare}
          trend={
            data?.trend?.responses
              ? {
                  value: formatTrend(data.trend.responses) || '',
                  isPositive: data.trend.responses > 0,
                }
              : undefined
          }
        />
        <MetricCard
          title="Tickets"
          value={(data?.totalTickets || 0).toLocaleString()}
          description="Total de atendimentos"
          icon={Users}
          trend={
            data?.trend?.tickets
              ? {
                  value: formatTrend(data.trend.tickets) || '',
                  isPositive: data.trend.tickets > 0,
                }
              : undefined
          }
        />
      </div>

      {/* Rankings + Volume in 2-col Grid (desktop) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Rankings - Compact Table */}
        <CompactRankingTable rankings={rankings} isLoading={rankingsQuery.isLoading} />

        {/* Message Volume Chart */}
        <MessageHistogramChart
          data={histogramQuery.data?.buckets || []}
          interval={histogramQuery.data?.interval || 'day'}
          isLoading={histogramQuery.isLoading}
        />
      </div>

      {/* Period Comparison (if comparing) */}
      {compareStartDate && compareEndDate && data && (
        <PeriodComparisonCard
          current={{
            frtAvg: data.frtAvg,
            tmrAvg: data.tmrAvg,
            messageCount: data.totalMessages,
            responseCount: data.totalResponses,
            ticketCount: data.totalTickets,
          }}
          previous={{
            frtAvg: 0,
            tmrAvg: 0,
            messageCount: 0,
            responseCount: 0,
            ticketCount: 0,
          }}
          periodLabel={`${new Date(compareStartDate).toLocaleDateString('pt-BR')} - ${new Date(compareEndDate).toLocaleDateString('pt-BR')}`}
        />
      )}

      <Separator />

      {/* Attendant Metrics */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Por Atendente</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exportMutation.isPending}
          >
            {exportMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Exportar CSV
          </Button>
        </div>

        {/* Mobile: Cards */}
        <div className="grid gap-3 md:hidden">
          {attendants.map((attendant) => (
            <AttendantMetricsCard
              key={attendant.attendantOdataId}
              name={attendant.attendantName}
              odataId={Number(attendant.attendantOdataId)}
              frt={attendant.frtAvg}
              tmr={attendant.tmrAvg}
              messageCount={attendant.totalMessages}
              responseCount={attendant.totalResponses}
              ticketCount={attendant.totalTickets}
            />
          ))}
        </div>

        {/* Desktop: Table */}
        <div className="hidden md:block">
          <AttendantMetricsTable data={attendants} isLoading={attendantsQuery.isLoading} />
        </div>
      </div>
    </div>
  )
}
